import { Slider as SliderMUI, styled } from '@mui/material';

import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';

type SliderBaseProps = {
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  formatValue?: (value: number) => string;
};

export type ControlledSliderProps = SliderBaseProps & {
  value: number;
  onChange: (value: number) => void;
  name?: never;
  control?: never;
};

export type FormSliderProps<T extends FieldValues> = SliderBaseProps &
  UseControllerProps<T> & {
    value?: never;
    onChange?: never;
  };

export type SliderProps<T extends FieldValues = FieldValues> =
  | ControlledSliderProps
  | FormSliderProps<T>;

const StyledSliderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  width: '100%',
  padding: theme.spacing(2, 3.5, 2, 6),
  border: '1px solid var(--stroke-stroke-primary-color)',
  borderRadius: theme.spacing(2),
}));

const StyledSlider = styled(SliderMUI)({
  flex: 1,
  color: 'var(--button-button-primary-color)',
});

const StyledValue = styled('span')(({ theme }) => ({
  minWidth: theme.spacing(7),
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--text-text-secondary-color)',
  textAlign: 'right',
}));

const defaultFormatValue = (value: number) => value.toFixed(1);

const renderSlider = ({
  value,
  onChange,
  min,
  max,
  step,
  disabled,
  formatValue,
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  formatValue: (value: number) => string;
}) => (
  <StyledSliderRow>
    <StyledSlider
      value={value}
      onChange={(_, newValue) => onChange(newValue as number)}
      min={min}
      max={max}
      step={step}
      disabled={disabled}
      valueLabelDisplay="auto"
    />
    <StyledValue>{formatValue(value)}</StyledValue>
  </StyledSliderRow>
);

export const Slider = <T extends FieldValues>({
  min = 0,
  max = 1,
  step = 0.1,
  disabled,
  formatValue = defaultFormatValue,
  ...props
}: SliderProps<T>) => {
  if ('control' in props && props.control && props.name) {
    const { name, control } = props;

    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const value = typeof field.value === 'number' ? field.value : min;

          return renderSlider({
            value,
            onChange: field.onChange,
            min,
            max,
            step,
            disabled,
            formatValue,
          });
        }}
      />
    );
  }

  const { value, onChange } = props as ControlledSliderProps;

  return renderSlider({
    value,
    onChange,
    min,
    max,
    step,
    disabled,
    formatValue,
  });
};
